import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import CombineOperator from "@mapmycustomers/shared/types/viewModel/internalModel/CombineOperator";
import {
  CombinedCondition,
  FilterCondition,
  FilterModel,
  PartialFilterModelUtil,
  SimpleCondition,
  SimpleConditionValue,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { MapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";

import { FILTER_ENTITY_TYPES } from "@app/component/filtering/utils/consts";
import { isCombinedCondition, isSimpleCondition } from "@app/util/viewModel/assert";

export const getRootOperator = (filterModel: FilterModel) => filterModel.operator;

export const getCombinedConditionForTag = (
  condition: CombinedCondition | undefined,
  tag: CombinedCondition["tag"]
): CombinedCondition | undefined => {
  const result = condition?.conditions.find(
    (condition) => isCombinedCondition(condition) && condition.tag === tag
  );
  return result as CombinedCondition | undefined;
};

export const getCombinedConditionIndexForTag = (
  condition: CombinedCondition | undefined,
  tag: CombinedCondition["tag"]
): number => {
  const result = condition?.conditions.findIndex(
    (condition) => isCombinedCondition(condition) && condition.tag === tag
  );
  return result ?? -1;
};

export const getSimpleConditionForField = (
  condition: CombinedCondition | undefined,
  fieldFilterName: string,
  withinTag?: CombinedCondition["tag"]
): SimpleCondition | undefined => {
  let conditions = condition?.conditions;

  if (withinTag) {
    conditions = getCombinedConditionForTag(condition, withinTag)?.conditions;
  }

  return conditions?.find(
    (condition) => isSimpleCondition(condition) && condition.fieldFilterName === fieldFilterName
  ) as SimpleCondition | undefined;
};

export const getSimpleConditionIndexForField = (
  condition: CombinedCondition | undefined,
  fieldFilterName: string
): number =>
  condition?.conditions.findIndex(
    (condition) => isSimpleCondition(condition) && condition.fieldFilterName === fieldFilterName
  ) ?? -1;

export const appendFilterCondition = (
  condition: CombinedCondition,
  newCondition: FilterCondition
): CombinedCondition => ({
  ...condition,
  conditions: [...condition.conditions, newCondition],
});

export const updateSimpleConditionAtIndex = (
  condition: CombinedCondition,
  index: number,
  replaceWith: SimpleCondition
): CombinedCondition => ({
  ...condition,
  conditions: condition.conditions.toSpliced(index, 1, replaceWith),
});

export const updateCombinedConditionAtIndex = (
  condition: CombinedCondition,
  index: number, // can be -1 to append
  replaceWith: CombinedCondition
): CombinedCondition => ({
  ...condition,
  conditions:
    index < 0
      ? [...condition.conditions, replaceWith]
      : condition.conditions.toSpliced(index, 1, replaceWith),
});

export const upsertSimpleConditions = (
  condition: CombinedCondition,
  ...newConditions: SimpleCondition[]
): CombinedCondition => {
  return newConditions.reduce((result, newCondition) => {
    const index = getSimpleConditionIndexForField(result, newCondition.fieldFilterName);
    if (index < 0) {
      return appendFilterCondition(result, newCondition);
    } else {
      return updateSimpleConditionAtIndex(result, index, newCondition);
    }
  }, condition);
};

export const upsertSimpleConditionsWithinTag = (
  condition: CombinedCondition,
  tag: CombinedCondition["tag"],
  ...newConditions: SimpleCondition[]
): CombinedCondition => {
  const indexOfConditionForTag = getCombinedConditionIndexForTag(condition, tag);
  const conditionToUpdate =
    indexOfConditionForTag >= 0
      ? (condition.conditions[indexOfConditionForTag] as CombinedCondition)
      : ({
          conditions: [],
          operator: "AND",
          tag,
        } as CombinedCondition);

  const result = upsertSimpleConditions(conditionToUpdate, ...newConditions);
  return updateCombinedConditionAtIndex(condition, indexOfConditionForTag, result);
};

export const removeSimpleConditionAtIndex = (
  condition: CombinedCondition,
  index: number
): CombinedCondition => ({
  ...condition,
  conditions: condition.conditions.toSpliced(index, 1),
});

export const removeSimpleConditionForFields = (
  condition: CombinedCondition,
  ...fieldFilterNames: IField["filterName"][]
): CombinedCondition => {
  return fieldFilterNames.reduce((result, fieldFilterName) => {
    return {
      ...result,
      conditions: result.conditions.filter(
        (condition) =>
          !isSimpleCondition(condition) || condition.fieldFilterName !== fieldFilterName
      ),
    };
  }, condition);
};

export const removeSimpleConditionForFieldsWithinTag = (
  condition: CombinedCondition,
  tag: CombinedCondition["tag"],
  ...fieldFilterNames: IField["filterName"][]
): CombinedCondition => {
  const conditionsIndex = getCombinedConditionIndexForTag(condition, tag);

  // nothing to remove
  if (conditionsIndex < 0) {
    return condition;
  }

  const result = removeSimpleConditionForFields(
    condition.conditions[conditionsIndex] as CombinedCondition,
    ...fieldFilterNames
  );
  return updateCombinedConditionAtIndex(condition, conditionsIndex, result);
};

export const extractSimpleConditionForField = (
  condition: CombinedCondition,
  fieldFilterName: IField["filterName"]
): [SimpleCondition | undefined, CombinedCondition] => {
  const index = getSimpleConditionIndexForField(condition, fieldFilterName);
  if (index < 0) {
    return [undefined, condition];
  }
  return [
    condition.conditions[index] as SimpleCondition,
    removeSimpleConditionAtIndex(condition, index),
  ];
};

export const extractSimpleConditionForFieldWithinTag = (
  condition: CombinedCondition,
  tag: CombinedCondition["tag"],
  fieldFilterName: IField["filterName"]
): [SimpleCondition | undefined, CombinedCondition] => {
  const conditionIndex = getCombinedConditionIndexForTag(condition, tag);
  if (conditionIndex < 0) {
    return [undefined, condition];
  }

  const result = extractSimpleConditionForField(
    condition.conditions[conditionIndex] as CombinedCondition,
    fieldFilterName
  );
  if (!result[0]) {
    return [undefined, condition];
  }

  const updatedCondition = updateCombinedConditionAtIndex(condition, conditionIndex, result[1]);
  return [result[0], updatedCondition];
};

export const createAndCondition = (
  conditions: FilterCondition[],
  tag?: CombinedCondition["tag"]
): CombinedCondition => ({
  conditions,
  operator: "AND",
  tag,
});

export const createOrCondition = (conditions: FilterCondition[]): CombinedCondition => ({
  conditions,
  operator: "OR",
});

export const createNotCondition = (conditions: FilterCondition[]): CombinedCondition => ({
  conditions,
  operator: "NOT",
});

export const createSimpleCondition = (
  fieldFilterName: IField["filterName"],
  operator: FilterOperator,
  value?: SimpleConditionValue
) => ({ fieldFilterName, operator, value }) as SimpleCondition;

// intended to be used with PartialNewFilterModel to indicate that certain
// field has no conditions.
export const createEmptyCondition = (fieldFilterName: IField["filterName"]) =>
  ({ fieldFilterName }) as PartialFilterModelUtil<SimpleCondition>;

export const createEmptyFilterModel = () => ({ conditions: [], operator: "AND" }) as FilterModel;

export const isFilterPresent = (
  filter: CombinedCondition,
  validator: (condition: SimpleCondition) => boolean
): boolean =>
  filter.conditions.some((condition) =>
    isCombinedCondition(condition) ? isFilterPresent(condition, validator) : validator(condition)
  );

export type FilterValidatorOptions = Partial<{
  allowedConjunctions: CombineOperator[];
  allowMany: boolean;
  allowNested: boolean;
  allowOne: boolean;
  allowZero: boolean;
  withinTag?: CombinedCondition["tag"];
}>;

export enum InvalidFilterReason {
  DISALLOWED_CONJUNCTION,
  TOO_MANY,
  USED_IN_GROUPS,
  NOT_PRESENT,
  UNSUPPORTED_CONDITION,
  UNEXPECTEDLY_PRESENT,
}

export const isFilterValid = (
  wholeFilter: FilterModel,
  fieldFilterName: string,
  conditionValidator: (condition: SimpleCondition) => boolean = () => true,
  options?: FilterValidatorOptions
): InvalidFilterReason | true => {
  const {
    allowedConjunctions = ["AND", "OR"],
    allowMany = false,
    allowNested = false,
    allowOne = true,
    allowZero = true,
    withinTag,
  } = options ?? {};

  let conditionsCount = 0;
  let hasNestedConditions = false;

  const filter =
    (withinTag ? getCombinedConditionForTag(wholeFilter, withinTag) : wholeFilter) ??
    createEmptyFilterModel();

  if (!allowedConjunctions.includes(filter.operator)) {
    return InvalidFilterReason.DISALLOWED_CONJUNCTION;
  }

  for (const condition of filter.conditions) {
    if (isCombinedCondition(condition)) {
      hasNestedConditions ||= isFilterPresent(
        condition,
        (condition) => condition.fieldFilterName === fieldFilterName
      );
    } else if (condition.fieldFilterName === fieldFilterName) {
      conditionsCount++;
      if (!conditionValidator(condition)) {
        return InvalidFilterReason.UNSUPPORTED_CONDITION;
      }
    }
  }

  if (conditionsCount === 0 && !allowZero) {
    return InvalidFilterReason.NOT_PRESENT;
  } else if (conditionsCount === 1 && !allowOne) {
    return InvalidFilterReason.UNEXPECTEDLY_PRESENT;
  } else if (conditionsCount > 1 && !allowMany) {
    return InvalidFilterReason.TOO_MANY;
  } else if (hasNestedConditions) {
    return allowNested || InvalidFilterReason.USED_IN_GROUPS;
  }

  return true;
};

export const getQuickFilterDetailsForField = (
  filter: FilterModel,
  fieldFilterName: string,
  conditionValidator?: (condition: SimpleCondition) => boolean,
  options?: FilterValidatorOptions
): [
  condition: SimpleCondition | undefined,
  disabled: boolean,
  disabledReason: InvalidFilterReason | undefined,
] => {
  const validationResult = isFilterValid(filter, fieldFilterName, conditionValidator, {
    allowedConjunctions: ["AND"],
    allowNested: true,
    ...options,
  });
  const fieldCondition =
    validationResult === true ? getSimpleConditionForField(filter, fieldFilterName) : undefined;

  return [
    fieldCondition,
    validationResult !== true,
    validationResult === true ? undefined : validationResult,
  ];
};

export const getQuickFilterDetailsForFieldWithinTag = (
  filter: FilterModel,
  tag: string,
  fieldFilterName: string,
  conditionValidator?: (condition: SimpleCondition) => boolean,
  options?: FilterValidatorOptions
): [
  condition: SimpleCondition | undefined,
  disabled: boolean,
  disabledReason: InvalidFilterReason | undefined,
] => {
  let condition = filter.conditions.find(
    (condition) => isCombinedCondition(condition) && condition.tag === tag
  ) as CombinedCondition | undefined;
  if (!condition) {
    condition = createAndCondition([]);
  }

  return getQuickFilterDetailsForField(condition, fieldFilterName, conditionValidator, options);
};

export const isMapFilterEmpty = (filter: MapFilterModel | undefined): boolean =>
  !filter || !FILTER_ENTITY_TYPES.some((entityType) => filter[entityType]?.conditions.length);
